/**
 * "One ongoing chat" with each Digital Team agent.
 *
 * 1. Thread memory — the conversation you last had with an agent, so opening
 *    them from the sidebar picks up where you left off instead of starting
 *    over. Remembered per browser; if there's nothing remembered, the agent's
 *    most recent chat from the server is used, then a fresh one.
 *
 * 2. Agent updates — the moments an agent needs you or a routine reported
 *    back, derived from real activity (the agent's Kanban commands and
 *    routine runs). The chat shows the ones you haven't seen as messages from
 *    the agent, and the sidebar shows how many are waiting. Nothing here is
 *    written by the agent itself: there is no endpoint yet for an agent (or a
 *    routine step) to post into your chat. [NEEDS VERIFICATION — backend]
 */
import { useCallback, useEffect, useState } from 'react';
import {
  listDwAgents, getAgentChats, getAgentKanbanTasks, getAgentRoutineRuns,
  type DwAgentRecord,
} from '../claudeagents/claudeAgentApi';
import { subscribeToKanbanUpdates } from '../../services/communicationService';
import { kanbanTsToIso } from '../../utils/dateTime';

// ── Thread memory ────────────────────────────────────────────────────────────
const THREADS_KEY = 'ig-agent-threads';

export interface AgentThread {
  conversationId: string;
  /** Claude-managed session id, so the agent keeps its context. */
  dwSessionId?: string | null;
}

function readThreads(): Record<string, AgentThread> {
  try {
    const raw = localStorage.getItem(THREADS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function rememberAgentThread(agentId: string, thread: AgentThread): void {
  try {
    const all = readThreads();
    const prev = all[agentId];
    if (prev && prev.conversationId === thread.conversationId && prev.dwSessionId === (thread.dwSessionId ?? prev.dwSessionId)) return;
    all[agentId] = { conversationId: thread.conversationId, dwSessionId: thread.dwSessionId ?? prev?.dwSessionId ?? null };
    localStorage.setItem(THREADS_KEY, JSON.stringify(all));
  } catch { /* per-browser convenience only */ }
}

function newConversationId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Where opening this agent should land: the remembered thread, else their
 *  latest chat on the server, else a new conversation. */
export async function resolveAgentThread(agentId: string): Promise<{ thread: AgentThread; isNew: boolean }> {
  const remembered = readThreads()[agentId];
  if (remembered) return { thread: remembered, isNew: false };
  try {
    const chats = await getAgentChats(agentId, 5);
    const latest = [...chats].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0];
    // Same convention as the Digital Workforce page's chat list: the chat id
    // is both the conversation route and the session to resume.
    if (latest) return { thread: { conversationId: latest.id, dwSessionId: latest.id }, isNew: false };
  } catch { /* fall through to a new chat */ }
  return { thread: { conversationId: newConversationId() }, isNew: true };
}

// ── Agent updates ────────────────────────────────────────────────────────────
const SEEN_KEY = 'ig-agent-updates-seen';
const SEEN_EVENT = 'ig-agent-updates-seen-changed';
const ROUTINE_POLL_MS = 60_000;
/** Updates older than this are history, not news. */
const MAX_AGE_MS = 7 * 24 * 60 * 60_000;

const TODO_ID = '11111111-1111-1111-1111-111111111111';
const REVIEW_ID = '33333333-3333-3333-3333-333333333333';

export type AgentUpdateKind = 'waiting' | 'review' | 'routine-done' | 'routine-failed';

export interface AgentUpdate {
  key: string;
  kind: AgentUpdateKind;
  /** ms epoch the thing happened. */
  at: number;
  title: string;
  /** Plain text detail (routine notes), already trimmed. */
  detail?: string;
  taskId?: string;
  taskNumber?: string;
  routineId?: string;
}

function readSeen(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function lastSeenUpdatesAt(agentId: string): number {
  return readSeen()[agentId] ?? 0;
}

export function markAgentUpdatesSeen(agentId: string): void {
  try {
    const all = readSeen();
    all[agentId] = Date.now();
    localStorage.setItem(SEEN_KEY, JSON.stringify(all));
    window.dispatchEvent(new Event(SEEN_EVENT));
  } catch { /* per-browser convenience only */ }
}

function toMs(value?: string | null): number {
  const iso = value ? kanbanTsToIso(value) : undefined;
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

function trimNotes(notes?: string): string | undefined {
  const text = (notes ?? '').replace(/[#*_`>]/g, '').replace(/\s+/g, ' ').trim();
  if (!text) return undefined;
  return text.length > 220 ? `${text.slice(0, 220).trimEnd()}…` : text;
}

/** Live updates for one agent (by record, so the stored name is at hand).
 *  `unread` counts updates newer than the last time you opened their chat. */
export function useAgentUpdates(agent: Pick<DwAgentRecord, 'agent_id' | 'name'> | null | undefined) {
  const [updates, setUpdates] = useState<AgentUpdate[]>([]);
  const [seenAt, setSeenAt] = useState(() => (agent ? lastSeenUpdatesAt(agent.agent_id) : 0));
  const agentId = agent?.agent_id ?? '';
  const agentName = agent?.name ?? '';

  const load = useCallback(() => {
    if (!agentId) return;
    const since = Date.now() - MAX_AGE_MS;
    Promise.all([
      getAgentKanbanTasks(agentId, agentName, 50).catch(() => []),
      getAgentRoutineRuns(agentId, agentName, 20).catch(() => []),
    ]).then(([tasks, runs]) => {
      const next: AgentUpdate[] = [];
      for (const t of tasks) {
        if (t.status_id !== TODO_ID && t.status_id !== REVIEW_ID) continue;
        const at = toMs(t.updated_at) || toMs(t.created_at);
        if (at < since) continue;
        next.push({
          key: `task:${t.id}:${t.status_id}`,
          kind: t.status_id === REVIEW_ID ? 'review' : 'waiting',
          at,
          title: t.title || 'Untitled command',
          taskId: String(t.id),
          taskNumber: t.task_number,
        });
      }
      for (const r of runs) {
        const status = (r.status ?? '').toLowerCase();
        if (status !== 'done' && status !== 'completed' && status !== 'success' && status !== 'failed' && status !== 'error') continue;
        const at = toMs(r.finished_at) || toMs(r.started_at);
        if (at < since) continue;
        next.push({
          key: `run:${r.id}`,
          kind: status === 'failed' || status === 'error' ? 'routine-failed' : 'routine-done',
          at,
          title: r.name || 'Routine',
          detail: trimNotes(r.notes),
          routineId: r.routine_id,
        });
      }
      next.sort((a, b) => a.at - b.at);
      setUpdates(next);
    });
  }, [agentId, agentName]);

  useEffect(() => {
    if (!agentId) { setUpdates([]); return; }
    setSeenAt(lastSeenUpdatesAt(agentId));
    load();
    const unsubscribe = subscribeToKanbanUpdates(load);
    const timer = window.setInterval(load, ROUTINE_POLL_MS);
    const onSeen = () => setSeenAt(lastSeenUpdatesAt(agentId));
    window.addEventListener(SEEN_EVENT, onSeen);
    return () => {
      unsubscribe();
      window.clearInterval(timer);
      window.removeEventListener(SEEN_EVENT, onSeen);
    };
  }, [agentId, load]);

  const unread = updates.filter(u => u.at > seenAt);
  return { updates, unread, seenAt };
}

/** Roster lookup for a hook that needs the stored agent name. */
export function useDwAgentRecord(agentId: string | null | undefined): DwAgentRecord | null {
  const [record, setRecord] = useState<DwAgentRecord | null>(null);
  useEffect(() => {
    if (!agentId) { setRecord(null); return; }
    let cancelled = false;
    listDwAgents()
      .then(list => { if (!cancelled) setRecord(list.find(a => a.agent_id === agentId) ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [agentId]);
  return record;
}
